import { supabase } from '../supabase/supabaseClient'

// ============================================================================
// USER PROFILE FUNCTIONS
// ============================================================================

export const createUserProfile = async (userId, referredBy = null) => {
  try {
    // subscription_tier is intentionally not sent: the column default ('free')
    // covers it, and the hardened column grant only allows user_id + referred_by
    const { data, error } = await supabase
      .from('user_profiles')
      .insert([
        {
          user_id: userId,
          ...(referredBy ? { referred_by: referredBy } : {})
        }
      ])
      .select()

    if (error) {
      // Handle duplicate key error - profile already exists
      // PostgreSQL code 23505 or HTTP 409 from Supabase
      if (error.code === '23505' || error.code === '409' || error.message?.includes('duplicate') || error.message?.includes('unique')) {
        // Fetch and return existing profile
        const { data: existingProfile } = await getUserProfile(userId)
        return { data: existingProfile, error: null }
      }

      console.error('Error creating user profile:', error)
      return { data: null, error: error.message }
    }

    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error in createUserProfile:', error)
    return { data: null, error: error.message }
  }
}

export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)

    // Handle PGRST116 error (no rows found) as normal case
    if (error && error.code === 'PGRST116') {
      return { data: null, error: null }
    }

    if (error) {
      console.error('Database error getting user profile:', error)
      return { data: null, error: error.message }
    }

    return { data: data && data.length > 0 ? data[0] : null, error: null }
  } catch (error) {
    console.error('Error in getUserProfile:', error)
    return { data: null, error: error.message }
  }
}

export const updateUserProfile = async (userId, updates) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ ...updates, updated_at: 'now()' })
      .eq('user_id', userId)
      .select()

    if (error) throw error
    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error updating user profile:', error)
    return { data: null, error: error.message }
  }
}

// ============================================================================
// USER FILTERS FUNCTIONS  
// ============================================================================

export const saveUserFilter = async (userId, pageType, filterName, filterData) => {
  try {
    const { data, error } = await supabase
      .from('user_filters')
      .insert([
        {
          user_id: userId,
          page_type: pageType, // 'ev' or 'arb'
          filter_name: filterName,
          filter_data: filterData
        }
      ])
      .select()

    if (error) throw error
    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error saving user filter:', error)
    return { data: null, error: error.message }
  }
}

export const getUserFilters = async (userId, pageType) => {
  try {
    const { data, error } = await supabase
      .from('user_filters')
      .select('*')
      .eq('user_id', userId)
      .eq('page_type', pageType)
      .order('created_at', { ascending: false })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error getting user filters:', error)
    return { data: [], error: error.message }
  }
}

export const deleteUserFilter = async (filterId) => {
  try {
    const { error } = await supabase
      .from('user_filters')
      .delete()
      .eq('id', filterId)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error deleting user filter:', error)
    return { error: error.message }
  }
}

export const updateUserFilter = async (filterId, updates) => {
  try {
    const { data, error } = await supabase
      .from('user_filters')
      .update(updates)
      .eq('id', filterId)
      .select()

    if (error) throw error
    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error updating user filter:', error)
    return { data: null, error: error.message }
  }
}

export const setDefaultFilter = async (userId, pageType, filterId) => {
  try {
    // The database trigger will automatically unset other defaults
    const { data, error } = await supabase
      .from('user_filters')
      .update({ is_default: true })
      .eq('id', filterId)
      .eq('user_id', userId)
      .eq('page_type', pageType)
      .select()

    if (error) throw error
    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error setting default filter:', error)
    return { data: null, error: error.message }
  }
}

export const unsetDefaultFilter = async (userId, pageType, filterId) => {
  try {
    const { data, error } = await supabase
      .from('user_filters')
      .update({ is_default: false })
      .eq('id', filterId)
      .eq('user_id', userId)
      .eq('page_type', pageType)
      .select()

    if (error) throw error
    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error unsetting default filter:', error)
    return { data: null, error: error.message }
  }
}

export const getDefaultFilter = async (userId, pageType) => {
  try {
    const { data, error } = await supabase
      .from('user_filters')
      .select('*')
      .eq('user_id', userId)
      .eq('page_type', pageType)
      .eq('is_default', true)
      .single()

    // Handle case where no default filter exists
    if (error && error.code === 'PGRST116') {
      return { data: null, error: null }
    }

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error getting default filter:', error)
    return { data: null, error: error.message }
  }
}

// ============================================================================
// USER BETS FUNCTIONS
// ============================================================================

export const saveUserBet = async (userId, betData) => {
  try {
    const { data, error } = await supabase
      .from('user_bets')
      .insert([
        {
          user_id: userId,
          bet_data: betData
        }
      ])
      .select()

    if (error) throw error
    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error saving user bet:', error)
    return { data: null, error: error.message }
  }
}

export const getUserBets = async (userId, limit = 15) => {
  try {
    const baseQuery = () => supabase
      .from('user_bets')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })

    if (limit !== null) {
      const { data, error } = await baseQuery().limit(limit)
      if (error) throw error
      return { data, error: null }
    }

    // null means get all. Supabase caps a response at 1000 rows, so page
    // through until a short page comes back.
    const PAGE = 1000
    const all = []
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await baseQuery().range(from, from + PAGE - 1)
      if (error) throw error
      all.push(...data)
      if (data.length < PAGE) break
    }
    return { data: all, error: null }
  } catch (error) {
    console.error('Error getting user bets:', error)
    return { data: [], error: error.message }
  }
}

// Pending EV bets only, for the tool pages' "already tracked" ticks. A settled
// bet's game is over, so it can never reappear on those pages; checking just
// the pending ones keeps the check complete without loading a whole history.
export const getPendingEvBets = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_bets')
      .select('bet_data')
      .eq('user_id', userId)
      .eq('bet_data->>type', 'EV')
      .eq('bet_data->>status', 'pending')

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error getting pending bets:', error)
    return { data: [], error: error.message }
  }
}

// Horse Racing Bot bets from the last day and a half, for the horse page's
// "already tracked" ticks. Settled ones count too: the page keeps showing a
// race after its result lands.
export const getRecentHorseBets = async (userId) => {
  try {
    const since = new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString()
    const { data, error } = await supabase
      .from('user_bets')
      .select('bet_data')
      .eq('user_id', userId)
      .in('bet_data->>bet_type_tag', ['horse_ev', 'horse_bonus'])
      .gte('bet_data->>commence_time', since)

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error getting horse bets:', error)
    return { data: [], error: error.message }
  }
}

// Recent bets of one type, for the ARB and MIDDLE pages' "already tracked"
// ticks. Those bets are saved already settled, so pending-only cannot find
// them; an opportunity rarely lists more than a few days out, so a week back
// covers anything that can still be on screen.
export const getRecentBetsOfType = async (userId, type, days = 7) => {
  try {
    const since = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString()
    const { data, error } = await supabase
      .from('user_bets')
      .select('bet_data')
      .eq('user_id', userId)
      .eq('bet_data->>type', type)
      .gte('created_at', since)

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error getting recent bets:', error)
    return { data: [], error: error.message }
  }
}

export const updateUserBet = async (betId, betData) => {
  try {
    const { data, error } = await supabase
      .from('user_bets')
      .update({ 
        bet_data: betData, 
        updated_at: 'now()' 
      })
      .eq('id', betId)
      .select()

    if (error) throw error
    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error updating user bet:', error)
    return { data: null, error: error.message }
  }
}

export const deleteUserBet = async (betId) => {
  try {
    const result = await supabase
      .from('user_bets')
      .delete()
      .eq('id', betId)

    if (result.error) throw result.error
    return { error: null }
  } catch (error) {
    console.error('Error deleting user bet:', error)
    return { error: error.message }
  }
}

// ============================================================================
// AFFILIATE FUNCTIONS
// ============================================================================

export const getAffiliate = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('affiliates')
      .select('*')
      .eq('user_id', userId)

    if (error) {
      console.error('Error fetching affiliate:', error)
      return { data: null, error: error.message }
    }

    return { data: data && data.length > 0 ? data[0] : null, error: null }
  } catch (error) {
    console.error('Error in getAffiliate:', error)
    return { data: null, error: error.message }
  }
}

export const createAffiliate = async ({ userId, code }) => {
  try {
    // status/commission_rate come from DB defaults (approved / 0.30);
    // RLS rejects any attempt to insert other values
    const { data, error } = await supabase
      .from('affiliates')
      .insert([
        {
          user_id: userId,
          code: code
        }
      ])
      .select()

    if (error) {
      if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique')) {
        // user_id unique = already an affiliate; code unique = code taken
        if (error.message?.includes('user_id')) {
          return { data: null, error: 'already_affiliate' }
        }
        return { data: null, error: 'code_taken' }
      }

      console.error('Error creating affiliate:', error)
      return { data: null, error: error.message }
    }

    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error in createAffiliate:', error)
    return { data: null, error: error.message }
  }
}

export const updateAffiliatePayoutDetails = async (userId, { method, email, bsb, accountNumber }) => {
  try {
    // The payout columns are the only ones the client grant allows updating.
    // All four are written each save so switching method leaves no stale details.
    const { data, error } = await supabase
      .from('affiliates')
      .update({
        payout_method: method,
        payout_email: method === 'paypal' ? email : null,
        payout_bsb: method === 'bank_au' ? bsb : null,
        payout_account_number: method === 'bank_au' ? accountNumber : null
      })
      .eq('user_id', userId)
      .select()

    if (error) {
      console.error('Error updating payout details:', error)
      return { data: null, error: error.message }
    }

    return { data: data && data.length > 0 ? data[0] : null, error: null }
  } catch (error) {
    console.error('Error in updateAffiliatePayoutDetails:', error)
    return { data: null, error: error.message }
  }
}

export const getCommissions = async (affiliateId) => {
  try {
    const { data, error } = await supabase
      .from('commissions')
      .select('*')
      .eq('affiliate_id', affiliateId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching commissions:', error)
      return { data: [], error: error.message }
    }

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error in getCommissions:', error)
    return { data: [], error: error.message }
  }
}

export const getReferredSignupCount = async () => {
  try {
    const { data, error } = await supabase.rpc('get_referred_signup_count')

    if (error) {
      console.error('Error fetching referred signup count:', error)
      return { data: 0, error: error.message }
    }

    return { data: data || 0, error: null }
  } catch (error) {
    console.error('Error in getReferredSignupCount:', error)
    return { data: 0, error: error.message }
  }
}

// ============================================================================
// ADMIN FUNCTIONS (RLS: only the admin user's uid gets rows back;
// for everyone else these return empty results, not errors)
// ============================================================================

export const getAllAffiliates = async () => {
  try {
    const { data, error } = await supabase
      .from('affiliates')
      .select('*')
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Error fetching all affiliates:', error)
      return { data: [], error: error.message }
    }

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error in getAllAffiliates:', error)
    return { data: [], error: error.message }
  }
}

export const getAllCommissions = async () => {
  try {
    const { data, error } = await supabase
      .from('commissions')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching all commissions:', error)
      return { data: [], error: error.message }
    }

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error in getAllCommissions:', error)
    return { data: [], error: error.message }
  }
}

export const getAllReferredSignups = async () => {
  try {
    // One row per referred signup; admin RLS policy required to see all rows
    const { data, error } = await supabase
      .from('user_profiles')
      .select('referred_by, email, subscription_tier, subscription_status, created_at')
      .not('referred_by', 'is', null)

    if (error) {
      console.error('Error fetching referred signups:', error)
      return { data: [], error: error.message }
    }

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error in getAllReferredSignups:', error)
    return { data: [], error: error.message }
  }
}

export const getAffiliateAccountEmails = async (userIds) => {
  try {
    // Account emails for the admin affiliates table (payout_email can differ).
    // Admin RLS policy on user_profiles is what makes this return rows.
    const { data, error } = await supabase
      .from('user_profiles')
      .select('user_id, email')
      .in('user_id', userIds)

    if (error) {
      console.error('Error fetching affiliate emails:', error)
      return { data: [], error: error.message }
    }

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error in getAffiliateAccountEmails:', error)
    return { data: [], error: error.message }
  }
}

export const markAffiliateCommissionsPaid = async (affiliateId) => {
  try {
    // Pays exactly the matured pending rows (the same set the payout query shows)
    const { data, error } = await supabase
      .from('commissions')
      .update({ status: 'paid', paid_at: new Date().toISOString() })
      .eq('affiliate_id', affiliateId)
      .eq('status', 'pending')
      .lt('matures_at', new Date().toISOString())
      .select()

    if (error) {
      console.error('Error marking commissions paid:', error)
      return { data: null, error: error.message }
    }

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error in markAffiliateCommissionsPaid:', error)
    return { data: null, error: error.message }
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const initializeUserData = async (userId, refCode = null) => {
  try {
    // Check if user profile exists, create if not
    const { data, error: profileError } = await getUserProfile(userId)

    if (profileError) {
      console.error('Error checking user profile:', profileError)
      return { error: profileError }
    }

    if (!data) {
      // Profile doesn't exist, create it
      const { error: createError } = await createUserProfile(userId, refCode)
      if (createError) {
        console.error('Error creating user profile:', createError)
        return { error: createError }
      }
    }

    return { error: null }
  } catch (error) {
    console.error('Error initializing user data:', error)
    return { error: error.message }
  }
}